import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Divider, Empty, Table, Button, Col, Drawer, Form, Input, Row, Space, message, Modal, Typography, InputNumber, Switch } from 'antd';
import { 
  getById as getIntegracionById,
  insert as insertIntegracion,
  update as updateIntegracion 
} from '../../../../apis/IntegracionApi';
import { 
  getAll as getDetallesByIntegracion,
  getById as getDetalleById,
  insert as insertDetalle,
  update as updateDetalle,
  drop as deleteDetalle,
  calculateFactorIntegracion
} from '../../../../apis/IntegracionDetalleApi';
import debounce from 'lodash/debounce';
import './Edit.scss';

const { Title } = Typography;
const { confirm } = Modal;

const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
};

const IntegracionForm = ({ title, id, open, finish }) => {
    const [form] = Form.useForm();
    const [detalleForm] = Form.useForm();
    const [ID, setID] = useState(id);
    const [openDetalle, setOpenDetalle] = useState(false);
    const [detalleData, setDetalleData] = useState([]);
    const [currentDetalle, setCurrentDetalle] = useState(null);
    const [loading, setLoading] = useState(false);

    const calcularFactorIntegracion = useCallback(async () => {
        try {
            const diasAguinaldo = detalleForm.getFieldValue('dias_aguinaldo');
            const diasVacaciones = detalleForm.getFieldValue('dias_vacaciones');
            const primaVacacional = detalleForm.getFieldValue('prima_vacacional');

            // Si aún no están los 3 campos, no llamamos al back
            if (diasAguinaldo === undefined || diasVacaciones === undefined || primaVacacional === undefined) {
                return;
            }

            const primaVacacionalFactor = Number(primaVacacional) / 100;

            const res = await calculateFactorIntegracion(diasAguinaldo, diasVacaciones, primaVacacionalFactor);

            if (res?.ok) {
            detalleForm.setFieldsValue({factor_integracion: res.payload});
            } else {
            message.error('No se pudo calcular el Factor de Integración');
            }
        } catch (error) {
            message.error('Error al calcular el Factor de Integración');
        }
    }, [detalleForm]);

    // Usamos useMemo para crear una versión debounceada de la función
    const calcularFactorIntegracionDebounced = useMemo(() => debounce(calcularFactorIntegracion, 300), [calcularFactorIntegracion]);

    useEffect(() => {
        return () => { calcularFactorIntegracionDebounced.cancel();};
    }, [calcularFactorIntegracionDebounced]);

    // Se dispara cuando cambian valores del Form de detalle
    const handleDetalleValuesChange = useCallback(
        (changedValues, allValues) => {
            // Si lo que cambió fue factor_integracion (lo seteamos), no recalculamos
            if ('factor_integracion' in changedValues) {
                return;
            }

            const { dias_aguinaldo, dias_vacaciones, prima_vacacional } = allValues;

            if (dias_aguinaldo === undefined || dias_vacaciones === undefined || prima_vacacional === undefined) {
                return;
            }
            calcularFactorIntegracionDebounced();
        },
        [calcularFactorIntegracionDebounced],
    );

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            if (ID > 0) {
                // Actualizar integración existente
                const response = await updateIntegracion({
                    id: ID,
                    ...values
                });
                if (response.ok) {
                    message.success('Integración actualizada correctamente');
                    finish(true, true);
                }
            } else {
                // Crear nueva integración
                const response = await insertIntegracion(values);
                if (response.ok) {
                    setID(response.payload);
                    message.success('Integración creada correctamente');
                    await fetchDetalleData(response.payload);
                }
            }
        } catch (error) {
            console.error('Error:', error);
            message.error('Ocurrió un error al guardar la integración');
        } finally {
            setLoading(false);
        }
    };

    const handleDetalleSubmit = async () => {
        try {
            setLoading(true);
            
            const formValues = await detalleForm.validateFields();
            const payload = {
                integracion_id: ID,
                septimo_S_vacaciones: formValues.septimo_S_vacaciones ?? false,
                septimo_S_prima: formValues.septimo_S_prima ?? false,
                antiguedad: formValues.antiguedad,
                dias_aguinaldo: formValues.dias_aguinaldo,
                dias_vacaciones: formValues.dias_vacaciones,
                prima_vacacional: formValues.prima_vacacional,
                dias_adicionales: formValues.dias_adicionales,
                prima_dominical: formValues.prima_dominical,
                factor_integracion: formValues.factor_integracion
            };
    
            if (currentDetalle) {
                const response = await updateDetalle({
                    id: currentDetalle.id,
                    ...payload
                });
                if (response.ok) {
                    message.success('Detalle actualizado correctamente');
                    setOpenDetalle(false);
                    await fetchDetalleData(ID);
                }
            } else {
                const response = await insertDetalle(payload);
                if (response.ok) {
                    message.success('Detalle creado correctamente');
                    setOpenDetalle(false);
                    await fetchDetalleData(ID);
                }
            }
        } catch (error) {
            console.error('Error:', error);
            message.error('Ocurrió un error al guardar el detalle');
        } finally {
            setLoading(false);
        }
    };

    const fetchDetalleData = async (integracionId) => {
        try {
            const response = await getDetallesByIntegracion(integracionId);
            if (response.ok) {
                const detallesFiltrados = response.payload.filter(d => d.integracion_id === integracionId);
                setDetalleData(detallesFiltrados);
            }
        } catch (error) {
            console.error('Error fetching detalles:', error);
            message.error('Error al cargar los detalles');
        }
    };

    const fetchData = async () => {
        if (ID > 0) {
            try {
                const response = await getIntegracionById(ID);
                if (response.ok) {
                    form.setFieldsValue(response.payload);
                    await fetchDetalleData(ID);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                message.error('Error al cargar los datos de integración');
            }
        } else {
            form.resetFields();
            setDetalleData([]);
        }
    };

    const handleDeleteDetalle = (id) => {
        confirm({
            title: '¿Estás seguro de eliminar este detalle?',
            icon: <ExclamationCircleFilled />,
            content: 'Esta acción no se puede deshacer',
            async onOk() {
                try {
                    const response = await deleteDetalle(id);
                    if (response.ok) {
                        message.success('Detalle eliminado correctamente');
                        await fetchDetalleData(ID);
                    }
                } catch (error) {
                    console.error('Error deleting:', error);
                    message.error('Error al eliminar el detalle');
                }
            }
        });
    };

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open, ID]);

    const columns = [
        {
            title: 'Antigüedad (años)',
            dataIndex: 'antiguedad',
            key: 'antiguedad',
        },
        {
            title: 'Días Aguinaldo',
            dataIndex: 'dias_aguinaldo',
            key: 'dias_aguinaldo',
        },
        {
            title: 'Días Vacaciones',
            dataIndex: 'dias_vacaciones',
            key: 'dias_vacaciones',
        },
        {
            title: '% Prima Vacacional',
            dataIndex: 'prima_vacacional',
            key: 'prima_vacacional',
            render: (value) => `${value}%`,
        },
        {
            title: 'Factor Integración',
            dataIndex: 'factor_integracion',
            key: 'factor_integracion',
            render: (value) => value.toFixed(4),
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => {
                        setCurrentDetalle(record);
                        detalleForm.setFieldsValue(record);
                        setOpenDetalle(true);
                    }}>Editar</Button>
                    <Button size="small" danger onClick={() => handleDeleteDetalle(record.id)}>Eliminar</Button>
                </Space>
            ),
        },
    ];

    return (
        <Drawer
            title={`${ID > 0 ? `(${ID}) ` : ''}${title}`}
            width={'80%'}
            placement="right"
            onClose={() => finish(false)}
            open={open}
            closable
            extra={[
                <Space>
                    <Button onClick={() => finish(false)}>Cancelar</Button>
                    <Button type="primary" loading={loading} onClick={handleSubmit}>
                        Guardar
                    </Button>
                </Space>
            ]}
        >
            <Form form={form} layout="vertical" {...formItemLayout}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="clave"
                            label="Clave"
                            rules={[{ required: true, message: 'Por favor ingresa la clave' }]}
                        >
                            <Input placeholder="Clave de integración" disabled={ID > 0} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="descripcion"
                            label="Descripción"
                            rules={[{ required: true, message: 'Por favor ingresa la descripción' }]}
                        >
                            <Input placeholder="Descripción de la integración" disabled={ID > 0}  />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">Detalles de Integración</Divider>

                <Button 
                    type="primary" 
                    onClick={() => {
                        setCurrentDetalle(null);
                        detalleForm.resetFields();
                        setOpenDetalle(true);
                    }}
                    disabled={!ID}
                    style={{ marginBottom: 16 }}
                >
                    Agregar Detalle
                </Button>

                <Table
                    columns={columns}
                    dataSource={detalleData}
                    rowKey="id"
                    locale={{
                        emptyText: <Empty description="No hay detalles registrados" />,
                    }}
                    loading={loading}
                />
            </Form>

            {/* Drawer para Detalle */}
            <Drawer
                title={currentDetalle ? 'Editar Detalle' : 'Agregar Detalle'}
                width={600}
                onClose={() => {
                    setOpenDetalle(false);
                    setCurrentDetalle(null);
                }}
                open={openDetalle}
                destroyOnClose
                extra={[
                    <Space>
                        <Button onClick={() => setOpenDetalle(false)}>Cancelar</Button>
                        <Button type="primary" loading={loading} onClick={handleDetalleSubmit}>
                            Guardar
                        </Button>
                    </Space>
                ]}
            >
                <Form
                    form={detalleForm}
                    layout="vertical"
                    onValuesChange={handleDetalleValuesChange}
                >
                    <Form.Item
                        name="antiguedad"
                        label="Antigüedad (años)"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="dias_aguinaldo"
                        label="Días de Aguinaldo"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                    >
                        <InputNumber 
                            min={0} 
                            style={{ width: '100%' }} 
                        />
                    </Form.Item>

                    <Form.Item
                        name="dias_vacaciones"
                        label="Días de Vacaciones"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                    >
                        <InputNumber 
                            min={0} 
                            style={{ width: '100%' }} 
                        />
                    </Form.Item>

                    <Form.Item
                        name="prima_vacacional"
                        label="Prima Vacacional (%)"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                    >
                        <InputNumber 
                            min={0} 
                            max={100} 
                            style={{ width: '100%' }} 
                        />
                    </Form.Item>

                    <Form.Item
                        name="dias_adicionales"
                        label="Días Adicionales"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="prima_dominical"
                        label="Prima Dominical (%)"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                    >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="septimo_S_vacaciones"
                        label="Séptimo Día en Vacaciones"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="septimo_S_prima"
                        label="Séptimo Día con Prima"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="factor_integracion"
                        label="Factor de Integración"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                        tooltip="Calculado automáticamente: 1 + (días aguinaldo / 365) + (días vacaciones × prima vacacional % / 365)"
                    >
                        <InputNumber 
                            min={0} 
                            step={0.0001} 
                            style={{ width: '100%' }} 
                            disabled
                        />
                    </Form.Item>
                </Form>
            </Drawer>
        </Drawer>
    );
};

export default IntegracionForm;
